import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import BulkDeleteBar from "../components/BulkDeleteBar";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import SortHeader from "../components/SortHeader";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  OrganizationsApi,
  type OrganizationListResponse,
  type OrganizationSortField,
  type SortDir,
} from "../api/organizations";
import Pagination from "../components/Pagination";
import logger from "../analytics/logger";
import { fmtDate } from "../utils/datetime";
import { readSort, writeSort, SORT_KEYS } from "../lib/tableSort";

type Form = {
  q: string;
};

const EMPTY_FORM: Form = { q: "" };

function formFromParams(p: URLSearchParams): Form {
  return {
    q: p.get("q") ?? "",
  };
}

const PREFS_KEY = "support_v2_organizations_table_v1";
const COL_MIN = 60;
const COL_MAX = 600;
const DEFAULT_WIDTHS: Record<OrganizationSortField, number> = {
  created_at: 140,
  name: 240,
};

type TablePrefs = {
  widths: Partial<Record<OrganizationSortField, number>>;
  sort?: OrganizationSortField;
  dir?: SortDir;
};

function loadPrefs(): TablePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { widths: {} };
    const parsed = JSON.parse(raw);
    return { widths: parsed.widths ?? {}, sort: parsed.sort, dir: parsed.dir };
  } catch {
    return { widths: {} };
  }
}

function savePrefs(p: TablePrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // non-fatal
  }
}

export default function Organizations() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const pageSize = Math.max(1, parseInt(params.get("page_size") ?? "40", 10) || 40);

  const [draft, setDraft] = useState<Form>(() => formFromParams(params));
  const [data, setData] = useState<OrganizationListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const confirm = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await OrganizationsApi.list({
          page,
          page_size: pageSize,
          q: params.get("q") ?? undefined,
          sort: (params.get("sort") as OrganizationSortField | null) ?? undefined,
          dir: (params.get("dir") as SortDir | null) ?? undefined,
        });
        if (!active) return;
        setData(d);
        setSelectedIds(new Set());
      } catch (e) {
        if (!active) return;
        logger.error("organizations_list_failed", e);
        setError(
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            "Failed to load organizations",
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [params, refetchTick, page, pageSize]);

  const visibleIds = data?.organizations.map((o) => o.id) ?? [];
  const selectedOnPage = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPage === visibleIds.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleAllOnPage = (checked: boolean) =>
    setSelectedIds(() => (checked ? new Set(visibleIds) : new Set()));
  const toggleOne = (id: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await OrganizationsApi.bulkDelete(ids);
      const deleted = res.deleted.length;
      const refused = res.refused.length;
      if (deleted) {
        toast({
          title: `Deleted ${deleted} organization${deleted === 1 ? "" : "s"}`,
          description: refused
            ? `${refused} skipped (still have creators)`
            : undefined,
          status: refused ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (refused) {
        toast({
          title: "Nothing deleted",
          description: `${refused} organization${refused === 1 ? "" : "s"} still have creators`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
      setSelectedIds(new Set());
      setRefetchTick((t) => t + 1);
      confirm.onClose();
    } catch (e) {
      logger.error("organizations_bulk_delete_failed", e);
      toast({
        title: "Delete failed",
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const refresh = () => {
    const next = new URLSearchParams();
    if (draft.q) next.set("q", draft.q);
    next.set("page", "1");
    setParams(next);
  };

  const reset = () => {
    setDraft(EMPTY_FORM);
    setParams(new URLSearchParams({ page: "1" }));
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.set("page", "1");
    setParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const [prefs, setPrefs] = useState<TablePrefs>(() => loadPrefs());

  // On mount only: if URL has no sort/dir, seed it from the remembered
  // sessionStorage sort (defaults to created_at desc if nothing remembered).
  useEffect(() => {
    if (!params.get("sort")) {
      const remembered = readSort(SORT_KEYS.organizations);
      const next = new URLSearchParams(params);
      next.set("sort", remembered.sort);
      next.set("dir", remembered.dir);
      setParams(next, { replace: true });
    }
    // Intentionally mount-only: re-seeding on every params change would fight
    // the user's own sort clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remembered = readSort(SORT_KEYS.organizations);
  const sort = (params.get("sort") as OrganizationSortField | null) ?? (remembered.sort as OrganizationSortField);
  const dir = (params.get("dir") as SortDir | null) ?? remembered.dir;

  // Persist the active sort to sessionStorage (the source of truth read back on
  // next mount via readSort). We deliberately do not mirror sort/dir into the
  // localStorage table prefs here — nothing reads prefs.sort/dir, and writing
  // React state in this effect is exactly what the set-state-in-effect rule flags.
  useEffect(() => {
    writeSort(SORT_KEYS.organizations, { sort, dir });
  }, [sort, dir]);

  const widthOf = (f: OrganizationSortField) => prefs.widths[f] ?? DEFAULT_WIDTHS[f];

  const setWidth = useCallback((f: OrganizationSortField, w: number) => {
    setPrefs((p) => {
      const next: TablePrefs = {
        ...p,
        widths: { ...p.widths, [f]: Math.max(COL_MIN, Math.min(COL_MAX, Math.round(w))) },
      };
      savePrefs(next);
      return next;
    });
  }, []);

  const toggleSort = (field: OrganizationSortField) => {
    const next = new URLSearchParams(params);
    if (sort === field) {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    } else {
      next.set("sort", field);
      next.set("dir", field === "created_at" ? "desc" : "asc");
    }
    next.set("page", "1");
    setParams(next);
  };

  const sortHeaderProps = (field: OrganizationSortField) => ({
    active: sort === field,
    dir,
    onToggle: () => toggleSort(field),
    width: widthOf(field),
    onResize: (w: number) => setWidth(field, w),
    onResetWidth: () => setWidth(field, DEFAULT_WIDTHS[field]),
  });

  return (
    <Box p={6}>
      <HStack justify="space-between" align="center" mb={4}>
        <Heading size="lg">Organizations</Heading>
        <Button size="sm" colorScheme="blue" onClick={() => navigate("/organizations/new")}>
          Add organization
        </Button>
      </HStack>

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        <HStack spacing={3} wrap="wrap">
          <Input
            size="sm"
            flex="1"
            minW="240px"
            placeholder="Search by name…"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <Button size="sm" colorScheme="blue" onClick={refresh}>
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </HStack>
      </Box>

      <BulkDeleteBar count={selectedIds.size} onDelete={confirm.onOpen} />
      <ConfirmDeleteDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onConfirm={doBulkDelete}
        count={selectedIds.size}
        entity="organization"
        busy={deleting}
      />

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={10} textAlign="center">
          <Spinner />
        </Box>
      ) : data ? (
        <>
          <Box overflowX="auto">
            <Table
              size="sm"
              variant="simple"
              sx={{ tableLayout: "fixed", width: "max-content" }}
            >
              <colgroup>
                <col style={{ width: "36px" }} />
                <col style={{ width: `${widthOf("created_at")}px` }} />
                <col style={{ width: `${widthOf("name")}px` }} />
              </colgroup>
              <Thead>
                <Tr>
                  <Th width="36px" px={2}>
                    <Checkbox
                      isChecked={allOnPageSelected}
                      isIndeterminate={someOnPageSelected}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      aria-label="Select all on this page"
                    />
                  </Th>
                  <SortHeader label="Created" {...sortHeaderProps("created_at")} />
                  <SortHeader label="Name" {...sortHeaderProps("name")} />
                </Tr>
              </Thead>
              <Tbody>
                {data.organizations.map((o) => (
                  <Tr
                    key={o.id}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => navigate(`/organizations/${o.id}`)}
                  >
                    <Td px={2} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={selectedIds.has(o.id)}
                        onChange={(e) => toggleOne(o.id, e.target.checked)}
                        aria-label={`Select organization ${o.name ?? ""}`}
                      />
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {fmtDate(o.created_at)}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {o.name ?? "—"}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}
    </Box>
  );
}
